// The portal navbar's "More ▾" group: the rarely used tabs, one click away.
//
// Renders nothing when it has no items (e.g. the executive bar, which keeps
// its curated set). Items are real links (<a href>) so every URL keeps
// working — middle-click, copy link, bookmarks — and the menu closes on
// pick, outside click, Escape and route change. The trigger lights up when
// the current page is one of its items; that item carries a check mark.
//
// Recorder rows (shared Guru recorder, data-action markup): opening the menu
// is `nav_more_open` (outcome open); picking an item is the same `nav_tab`
// row a top-row tab records, with the tab id as the chosen value.

import { useCallback, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { NAV_DROPDOWN_TOP, navDropdownAnchor, type NavDropdownAnchor } from './navDropdownAnchor';
import styles from './PortalMoreMenu.module.css';

/** The dropdown's CSS min-width (PortalMoreMenu.module.css .menu), for edge clamping. */
const MENU_WIDTH = 220;

export interface PortalMoreMenuItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  title?: string;
}

interface PortalMoreMenuProps {
  items: readonly PortalMoreMenuItem[];
  /** The navbar's resolved active tab id ('' when nothing is lit). */
  activeTab: string;
}

const caret = (
  <svg
    className={styles.caret}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export default function PortalMoreMenu({ items, activeTab }: PortalMoreMenuProps) {
  const location = useLocation();
  // The menu is open only for the path it was opened on, so any navigation
  // (from inside the menu or anywhere else) closes it with no effect needed.
  // `anchor` is where the trigger sat when the menu opened: the dropdown is
  // position: fixed (pinned to the viewport under the 56px bar) so a dense,
  // sideways-scrolling tab row cannot clip it.
  const [opened, setOpened] = useState<{ path: string; anchor: NavDropdownAnchor } | null>(null);
  const open = opened?.path === location.pathname;
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpened(null), []);
  const toggle = () => {
    if (open) {
      setOpened(null);
      return;
    }
    setOpened({ path: location.pathname, anchor: navDropdownAnchor(triggerRef.current, MENU_WIDTH) });
  };

  useClickOutside(wrapRef, close, open);
  useEscapeKey(close, open);

  if (items.length === 0) return null;

  const activeItem = items.find((item) => item.id === activeTab);

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.tab} ${activeItem ? styles.tabActive : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={activeItem ? `More — ${activeItem.label}` : 'More'}
        title={
          activeItem
            ? `More — you are on ${activeItem.label}`
            : `More: ${items.map((item) => item.label).join(', ')}`
        }
        onClick={toggle}
        data-action="nav_more_open"
        data-outcome="open"
      >
        More
        {caret}
      </button>

      {open && (
        <div className={styles.menu} role="menu" style={{ top: NAV_DROPDOWN_TOP, right: opened?.anchor.right }}>
          {items.map((item) => {
            const isActive = item.id === activeTab;
            return (
              <Link
                key={item.id}
                to={item.path}
                role="menuitem"
                className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
                aria-current={isActive ? 'page' : undefined}
                title={item.title}
                onClick={close}
                data-action="nav_tab"
                data-value={item.id}
                data-record-value="tab"
                data-outcome="navigate"
              >
                <span className={styles.itemIcon}>{item.icon}</span>
                <span className={styles.itemLabel}>{item.label}</span>
                {isActive && (
                  <svg
                    className={styles.check}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
